import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaBalanceScale,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaSync,
  FaDownload,
  FaEye,
  FaFilter,
  FaSearch,
  FaCalendarAlt
} from 'react-icons/fa';
import { paymentService } from '../../../../services/paymentService';
import { toast } from 'react-toastify';
import { formatPrice } from '../../../../utils/currency';

const Reconciliation = () => {
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: '7d',
    gateway: 'all',
    status: 'all'
  });
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);

  useEffect(() => {
    fetchReconciliationData();
  }, [filters]);

  const fetchReconciliationData = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getReconciliationData(filters);
      setReconciliationData(data);
      setDiscrepancies(data?.discrepancies || []);
    } catch (error) {
      console.error('Failed to fetch reconciliation data:', error);
      if (error.message?.includes('404') || error.message?.includes('Failed to fetch')) {
        toast.error('Reconciliation routes not available. Please ensure the backend server is restarted.');
      } else {
        toast.error('Failed to load reconciliation data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRunReconciliation = async () => {
    try {
      setReconciling(true);
      const result = await paymentService.runReconciliation({
        dateRange: filters.dateRange,
        gateway: filters.gateway !== 'all' ? filters.gateway : undefined
      });
      toast.success('Reconciliation completed successfully!');
      fetchReconciliationData();
    } catch (error) {
      toast.error('Reconciliation failed: ' + error.message);
    } finally {
      setReconciling(false);
    }
  };

  const handleResolveDiscrepancy = async (discrepancyId, resolution) => {
    try {
      await paymentService.resolveDiscrepancy(discrepancyId, resolution);
      toast.success('Discrepancy resolved successfully!');
      fetchReconciliationData();
      setSelectedDiscrepancy(null);
    } catch (error) {
      toast.error('Failed to resolve discrepancy: ' + error.message);
    }
  };

  const getDiscrepancyTypeBadge = (type) => {
    const config = {
      missing: { bg: 'bg-red-100', text: 'text-red-800', icon: FaTimesCircle },
      mismatch: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FaExclamationTriangle },
      duplicate: { bg: 'bg-orange-100', text: 'text-orange-800', icon: FaExclamationTriangle },
      resolved: { bg: 'bg-green-100', text: 'text-green-800', icon: FaCheckCircle }
    };
    const item = config[type] || config.mismatch;
    const Icon = item.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${item.bg} ${item.text}`}>
        <Icon className="text-xs" />
        {type}
      </span>
    );
  };

  if (loading && !reconciliationData) {
    return (
      <div className="flex items-center justify-center p-20">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Payment Reconciliation</h2>
          <p className="text-sm text-gray-600 mt-1">Match and verify payment transactions with gateway records</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRunReconciliation}
            disabled={reconciling}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {reconciling ? (
              <>
                <FaSpinner className="animate-spin" /> Reconciling...
              </>
            ) : (
              <>
                <FaSync /> Run Reconciliation
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="1d">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gateway</label>
            <select
              value={filters.gateway}
              onChange={(e) => setFilters({ ...filters, gateway: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Gateways</option>
              <option value="razorpay">Razorpay</option>
              <option value="phonepe">PhonePe</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="matched">Matched</option>
              <option value="discrepancy">Discrepancies</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {reconciliationData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-green-700 mb-1">Matched</p>
                <p className="text-2xl font-bold text-green-900">
                  {reconciliationData.matched || 0}
                </p>
              </div>
              <FaCheckCircle className="text-2xl text-green-600" />
            </div>
          </div>
          <div className="bg-red-50 rounded-lg p-6 border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-red-700 mb-1">Discrepancies</p>
                <p className="text-2xl font-bold text-red-900">
                  {reconciliationData.discrepancies?.length || 0}
                </p>
              </div>
              <FaExclamationTriangle className="text-2xl text-red-600" />
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-yellow-700 mb-1">Pending</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {reconciliationData.pending || 0}
                </p>
              </div>
              <FaSpinner className="text-2xl text-yellow-600" />
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-700 mb-1">Total Amount</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatPrice(reconciliationData.totalAmount || 0)}
                </p>
              </div>
              <FaBalanceScale className="text-2xl text-blue-600" />
            </div>
          </div>
        </div>
      )}

      {/* Discrepancies Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Discrepancies</h3>
          <p className="text-sm text-gray-600 mt-1">Transactions that don't match between system and gateway</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gateway</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">System Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gateway Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difference</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {discrepancies.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    No discrepancies found. All transactions are matched!
                  </td>
                </tr>
              ) : (
                discrepancies.map((disc) => (
                  <tr key={disc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">{disc.transactionId}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{disc.orderId}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{disc.gateway}</td>
                    <td className="px-6 py-4">{getDiscrepancyTypeBadge(disc.type)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{formatPrice(disc.systemAmount || 0)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{formatPrice(disc.gatewayAmount || 0)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-red-600">
                      {formatPrice(Math.abs((disc.systemAmount || 0) - (disc.gatewayAmount || 0)))}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {new Date(disc.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedDiscrepancy(disc)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Discrepancy Detail Modal */}
      <AnimatePresence>
        {selectedDiscrepancy && (
          <DiscrepancyModal
            discrepancy={selectedDiscrepancy}
            onClose={() => setSelectedDiscrepancy(null)}
            onResolve={handleResolveDiscrepancy}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const DiscrepancyModal = ({ discrepancy, onClose, onResolve }) => {
  const [resolution, setResolution] = useState({
    action: 'accept_system', // accept_system, accept_gateway, manual
    notes: ''
  });
  const [resolving, setResolving] = useState(false);

  const handleResolve = async () => {
    try {
      setResolving(true);
      await onResolve(discrepancy.id, resolution);
    } finally {
      setResolving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Discrepancy Details</h3>
          
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                <p className="text-sm text-gray-900">{discrepancy.transactionId}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                <p className="text-sm text-gray-900">{discrepancy.orderId}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gateway</label>
                <p className="text-sm text-gray-900">{discrepancy.gateway}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <p className="text-sm text-gray-900">{discrepancy.type}</p>
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-semibold text-gray-900 mb-3">Amount Comparison</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <label className="block text-xs font-medium text-blue-700 mb-1">System Amount</label>
                  <p className="text-lg font-bold text-blue-900">{formatPrice(discrepancy.systemAmount || 0)}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <label className="block text-xs font-medium text-green-700 mb-1">Gateway Amount</label>
                  <p className="text-lg font-bold text-green-900">{formatPrice(discrepancy.gatewayAmount || 0)}</p>
                </div>
              </div>
              <div className="mt-3 bg-red-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-red-700 mb-1">Difference</label>
                <p className="text-lg font-bold text-red-900">
                  {formatPrice(Math.abs((discrepancy.systemAmount || 0) - (discrepancy.gatewayAmount || 0)))}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-semibold text-gray-900 mb-3">Resolution</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Action</label>
                <select
                  value={resolution.action}
                  onChange={(e) => setResolution({ ...resolution, action: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="accept_system">Accept System Amount</option>
                  <option value="accept_gateway">Accept Gateway Amount</option>
                  <option value="manual">Manual Adjustment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={resolution.notes}
                  onChange={(e) => setResolution({ ...resolution, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add resolution notes..."
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleResolve}
              disabled={resolving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {resolving ? 'Resolving...' : 'Resolve Discrepancy'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Reconciliation;


